// Jax Counter Strike slam VFX — Jax leaps to the slam point (between the
// closest two enemies), slams the ground with a purple shockwave, then dashes
// back to his slot. The slam ring + popup callback fire when he lands.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

const DASH_IN_MS = 200;
const SLAM_MS = 480;
const DASH_OUT_MS = 220;
const CRACK_COUNT = 8;

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

export class JaxSlamVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private slamPx: Point,
    private radius: number,
    private onSlamImpact: () => void,
  ) {}

  update(dt: number): void {
    this.elapsed += dt;

    if (this.elapsed < DASH_IN_MS) {
      const t = easeInOut(this.elapsed / DASH_IN_MS);
      this.visual.x = this.startPx.x + (this.slamPx.x - this.startPx.x) * t;
      this.visual.y = this.startPx.y + (this.slamPx.y - this.startPx.y) * t;
      return;
    }

    if (this.elapsed < DASH_IN_MS + SLAM_MS) {
      this.visual.x = this.slamPx.x;
      this.visual.y = this.slamPx.y;
      if (!this.impactFired) {
        this.impactFired = true;
        this.onSlamImpact();
      }
      return;
    }

    const t = easeInOut(Math.min(1, (this.elapsed - DASH_IN_MS - SLAM_MS) / DASH_OUT_MS));
    this.visual.x = this.slamPx.x + (this.startPx.x - this.slamPx.x) * t;
    this.visual.y = this.slamPx.y + (this.startPx.y - this.slamPx.y) * t;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed < DASH_IN_MS) return;
    const slamElapsed = this.elapsed - DASH_IN_MS;
    if (slamElapsed > SLAM_MS) return;

    const t = slamElapsed / SLAM_MS;
    const expandT = Easing.easeOutBack(Math.min(t * 1.4, 1));
    const fade = t < 0.45 ? 1 : Math.max(0, 1 - (t - 0.45) / 0.55);

    ctx.save();

    ctx.globalAlpha = fade * 0.22;
    ctx.fillStyle = "#7c3aed";
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(this.slamPx.x, this.slamPx.y, this.radius * expandT, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = fade * 0.9;
    ctx.strokeStyle = "#a855f7";
    ctx.lineWidth = 5;
    ctx.shadowColor = "#9333ea";
    ctx.shadowBlur = 26;
    ctx.beginPath();
    ctx.arc(this.slamPx.x, this.slamPx.y, this.radius * expandT, 0, Math.PI * 2);
    ctx.stroke();

    ctx.globalAlpha = fade * 0.55;
    ctx.strokeStyle = "#e9d5ff";
    ctx.lineWidth = 2;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.arc(this.slamPx.x, this.slamPx.y, this.radius * expandT * 0.65, 0, Math.PI * 2);
    ctx.stroke();

    ctx.globalAlpha = fade * 0.75;
    ctx.strokeStyle = "#c084fc";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#a855f7";
    ctx.shadowBlur = 10;
    for (let i = 0; i < CRACK_COUNT; i++) {
      const angle = (i / CRACK_COUNT) * Math.PI * 2 + 0.2;
      const inner = this.radius * 0.12;
      const outer = this.radius * expandT * 1.1;
      ctx.beginPath();
      ctx.moveTo(this.slamPx.x + Math.cos(angle) * inner, this.slamPx.y + Math.sin(angle) * inner);
      ctx.lineTo(this.slamPx.x + Math.cos(angle) * outer, this.slamPx.y + Math.sin(angle) * outer);
      ctx.stroke();
    }

    ctx.shadowBlur = 8;
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2 + t * 1.5;
      const r = this.radius * expandT;
      ctx.globalAlpha = fade * 0.9;
      ctx.fillStyle = "#f0abfc";
      ctx.beginPath();
      ctx.arc(
        this.slamPx.x + Math.cos(angle) * r,
        this.slamPx.y + Math.sin(angle) * r,
        2.5, 0, Math.PI * 2,
      );
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= DASH_IN_MS + SLAM_MS + DASH_OUT_MS;
  }
}
